import React, { useState, useEffect } from 'react';
import { styles } from '../../../utils/styles';
import { CategoryIcon, OverFlowIcon } from 'assests/GmsIcons';
import { useEditor } from '../../../contexts/EditorContext';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import { CategoryDetails } from './CategoryDetails';
import { ProductListing } from './ProductListing';
import { ProductDetails } from './ProductDetails';
import { useModule } from '../../../contexts/ModuleContext';
import { getProductsByCategory } from '../../../services/ApiHelper';
import {SortableAttribute, SortConfig} from "../../../types/sortconfig.types";

type CategoryConfigProps = {
    id?: string;
    projectId: string;
    categoryId: string;
    categoryName?: string;
    onConfigChange?: (config: any) => void;
}

export const CategoryConfig= ({ id, projectId, categoryId, categoryName,onConfigChange }: CategoryConfigProps) => {
    const baseId = id || 'category-config';
    const { containers, editorState, setContainers, uiModel } = useEditor();
    const { getComponentDefinition } = useComponentRegistry();
    const { industryId } = useModule();
    
    const categoryDef = getComponentDefinition(categoryId);
    
    const [categoryDetailsExpanded, setCategoryDetailsExpanded] = useState(true);
    const [productListingExpanded, setProductListingExpanded] = useState(false);
    const [productDetailsExpanded, setProductDetailsExpanded] = useState(false);
    
    const [label, setLabel] = useState(categoryDef?.name || categoryName || 'AC Motors');
    const [showTitle, setShowTitle] = useState(true);
    const [titleText, setTitleText] = useState('Select AC Motors');
    const [showCategoryDescription, setShowCategoryDescription] = useState(true);
    const [descriptionText, setDescriptionText] = useState('Compatibility based on selections in your build');
    const [showThumbnail, setShowThumbnail] = useState(true);
    const [imageDisplay, setImageDisplay] = useState<'Fit' | 'Fill' | 'Stretch'>('Fit');
    const [showSortByAttributes, setShowSortByAttributes] = useState(true);
    const [showInStock, setShowInStock] = useState(true);
    const [inStockText, setInStockText] = useState('Show In Stock only');
    const [selectByDefault, setSelectByDefault] = useState(false);
    const [hideIfNoProducts, setHideIfNoProducts] = useState(false);

    const [sortConfig, setSortConfig] = useState<SortConfig>({
        defaultSortOptions: [
            { id: 'default_recommended', label: 'Recommended', direction: 'desc' },
            { id: 'default_price_asc', label: 'Price (Low to High)', field: 'min_ad_price', direction: 'asc' },
            { id: 'default_price_desc', label: 'Price (High to Low)', field: 'min_ad_price', direction: 'desc' },
            { id: 'default_name_asc', label: 'Alphabetical (A to Z)', field: 'name', direction: 'asc' },
            { id: 'default_name_desc', label: 'Alphabetical (Z to A)', field: 'name', direction: 'desc' }
        ],
        sortableAttributes: [],
        selectedDefaultSortId: 'default_recommended'
    });
    
    const [attributes, setAttributes] = useState<any[]>([]);
    
    // State for Product Listing section
    const [style, setStyle] = useState<'Grid' | 'List'>('Grid');
    const [productsPerRow, setProductsPerRow] = useState('4');
    const [productsLoading, setProductsLoading] = useState<'Infinite Scroll' | 'Load More Button'>('Infinite Scroll');
    const [scrollBarAfterRows, setScrollBarAfterRows] = useState('4');
    const [showButtonAfterProducts, setShowButtonAfterProducts] = useState('20');
    const [scrollBarAfterProducts, setScrollBarAfterProducts] = useState('20');
    
    // State for Product Details section
    const [showModelNumber, setShowModelNumber] = useState(true);
    const [showProductDescription, setShowProductDescription] = useState(true);
    const [descriptionStyle, setDescriptionStyle] = useState('Partly');
    const [showPrice, setShowPrice] = useState(true);
    const [showAvailabilityStatus, setShowAvailabilityStatus] = useState(true);
    const [inStockLabel, setInStockLabel] = useState('In Stock');
    const [backOrderLabel, setBackOrderLabel] = useState('Enter Label');
    const [showVariants, setShowVariants] = useState(false);
    const [showNumberOfVariants, setShowNumberOfVariants] = useState(false);
    const [showCategory, setShowCategory] = useState(false);
    const [showManufacturer, setShowManufacturer] = useState(false);
    const [productDetailsTextAlignment, setProductDetailsTextAlignment] = useState<'Left' | 'Centre' | 'Right'>('Left');
    const [showButtonText, setShowButtonText] = useState(true);
    const [buttonLabel, setButtonLabel] = useState('Add to Cart');
    const [showButtonIcon, setShowButtonIcon] = useState(false);
    const [buttonPlacement, setButtonPlacement] = useState<'Left' | 'Centre' | 'Right'>('Left');
    const [buttonIconUrl, setButtonIconUrl] = useState<string>('');
    const [categoryImageUrl, setCategoryImageUrl] = useState<string>('');

    useEffect(() => {
        if (categoryId && industryId) {
            fetchAttributes();
        }
    }, [categoryId, industryId]);

    const fetchAttributes = async () => {
        try {
            let wrapperId = null;
            let actualCategoryId = null;
            
            if (categoryDef?.dataConfiguration) {
                const dataConfig = categoryDef.dataConfiguration;
                
                wrapperId = dataConfig.id || categoryId;
                actualCategoryId = dataConfig.categoryId || null;
                
                if (dataConfig.isEmpty || !actualCategoryId) {
                    console.warn(`Category "${dataConfig.wrapperName}" (Wrapper ID: ${wrapperId}) has no category ID. Cannot fetch products.`);
                    setAttributes([]);
                    return;
                }
            } else {
                actualCategoryId = categoryId;
            }

            const response = await getProductsByCategory(industryId, actualCategoryId);
            if (response && response.done && response.data) {
                setAttributes(response.data.attributes || []);
            }
        } catch (error) {
            console.error('Error fetching attributes:', error);
            setAttributes([]);
        }
    };
    
    useEffect(() => {
        if (categoryId && editorState.activePageId && containers[editorState.activePageId]) {
            const pageContainers = containers[editorState.activePageId];
            
            const findCategoryConfig = (containerList: any[]): any => {
                for (const container of containerList) {
                    const category = container.content.items?.find((item: any) => 
                        item.type === 'category' && item.id === categoryId
                    );
                    
                    if (category?.configuration) {
                        return category.configuration;
                    }
                    
                    if (container.content.nestedContainers?.length > 0) {
                        const found = findCategoryConfig(container.content.nestedContainers);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const categoryConfig = findCategoryConfig(pageContainers);
            if (categoryConfig) {
                setLabel(categoryConfig.label || label);
                setShowTitle(categoryConfig.showTitle ?? true);
                setTitleText(categoryConfig.titleText || titleText);
                setShowCategoryDescription(categoryConfig.showCategoryDescription ?? true);
                setDescriptionText(categoryConfig.descriptionText || descriptionText);
                setShowThumbnail(categoryConfig.showThumbnail ?? true);
                setImageDisplay(categoryConfig.imageDisplay || 'Fit');
                setShowSortByAttributes(categoryConfig.showSortByAttributes ?? true);
                setShowInStock(categoryConfig.showInStock ?? true);
                setInStockText(categoryConfig.inStockText || inStockText);
                setSelectByDefault(categoryConfig.selectByDefault ?? false);
                setHideIfNoProducts(categoryConfig.hideIfNoProducts ?? false);
                
                // Product Listing
                setStyle(categoryConfig.style || 'Grid');
                setProductsPerRow(categoryConfig.productsPerRow || '4');
                setProductsLoading(categoryConfig.productsLoading || 'Infinite Scroll');
                setScrollBarAfterRows(categoryConfig.scrollBarAfterRows || '4');
                setShowButtonAfterProducts(categoryConfig.showButtonAfterProducts || '20');
                setScrollBarAfterProducts(categoryConfig.scrollBarAfterProducts || '20');
                
                // Product Details
                setShowModelNumber(categoryConfig.showModelNumber ?? true);
                setShowProductDescription(categoryConfig.showProductDescription ?? true);
                setDescriptionStyle(categoryConfig.descriptionStyle || 'Partly');
                setShowPrice(categoryConfig.showPrice ?? true);
                setShowAvailabilityStatus(categoryConfig.showAvailabilityStatus ?? true);
                setInStockLabel(categoryConfig.inStockLabel || 'In Stock');
                setBackOrderLabel(categoryConfig.backOrderLabel || 'Enter Label');
                setShowVariants(categoryConfig.showVariants ?? false);
                setShowNumberOfVariants(categoryConfig.showNumberOfVariants ?? false);
                setShowCategory(categoryConfig.showCategory ?? false);
                setShowManufacturer(categoryConfig.showManufacturer ?? false);
                setProductDetailsTextAlignment(categoryConfig.productDetailsTextAlignment || 'Left');
                setShowButtonText(categoryConfig.showButtonText ?? true);
                setButtonLabel(categoryConfig.buttonLabel || 'Add to Cart');
                setShowButtonIcon(categoryConfig.showButtonIcon ?? false);
                setButtonPlacement(categoryConfig.buttonPlacement || 'Left');
                setButtonIconUrl(categoryConfig.buttonIconUrl || '');
                setCategoryImageUrl(categoryConfig.categoryImageUrl || '');

                if (categoryConfig.sortConfig) {
                    const existingSortConfig = categoryConfig.sortConfig;
                    if (!existingSortConfig.defaultSortOptions || existingSortConfig.defaultSortOptions.length === 0) {
                        existingSortConfig.defaultSortOptions = sortConfig.defaultSortOptions;
                    }
                    setSortConfig(existingSortConfig);
                }
            }
        }
    }, [categoryId, editorState.activePageId]);
    
    const handleConfigChange = (key: string, value: any) => {
        switch (key) {
            case 'label': setLabel(value); break;
            case 'showTitle': setShowTitle(value); break;
            case 'titleText': setTitleText(value); break;
            case 'showCategoryDescription': setShowCategoryDescription(value); break;
            case 'descriptionText': setDescriptionText(value); break;
            case 'showThumbnail': setShowThumbnail(value); break;
            case 'imageDisplay': setImageDisplay(value); break;
            case 'showSortByAttributes': setShowSortByAttributes(value); break;
            case 'sortConfig': setSortConfig(value); break;
            case 'showInStock': setShowInStock(value); break;
            case 'inStockText': setInStockText(value); break;
            case 'selectByDefault': setSelectByDefault(value); break;
            case 'hideIfNoProducts': setHideIfNoProducts(value); break;
            case 'style': setStyle(value); break;
            case 'productsPerRow': setProductsPerRow(value); break;
            case 'productsLoading': setProductsLoading(value); break;
            case 'scrollBarAfterRows': setScrollBarAfterRows(value); break;
            case 'showButtonAfterProducts': setShowButtonAfterProducts(value); break;
            case 'scrollBarAfterProducts': setScrollBarAfterProducts(value); break;
            case 'showModelNumber': setShowModelNumber(value); break;
            case 'showProductDescription': setShowProductDescription(value); break;
            case 'descriptionStyle': setDescriptionStyle(value); break;
            case 'showPrice': setShowPrice(value); break;
            case 'showAvailabilityStatus': setShowAvailabilityStatus(value); break;
            case 'inStockLabel': setInStockLabel(value); break;
            case 'backOrderLabel': setBackOrderLabel(value); break;
            case 'showVariants': setShowVariants(value); break;
            case 'showNumberOfVariants': setShowNumberOfVariants(value); break;
            case 'showCategory': setShowCategory(value); break;
            case 'showManufacturer': setShowManufacturer(value); break;
            case 'productDetailsTextAlignment': setProductDetailsTextAlignment(value as 'Left' | 'Centre' | 'Right'); break;
            case 'showButtonText': setShowButtonText(value); break;
            case 'buttonLabel': setButtonLabel(value); break;
            case 'showButtonIcon': setShowButtonIcon(value); break;
            case 'buttonPlacement': setButtonPlacement(value as 'Left' | 'Centre' | 'Right'); break;
            case 'buttonIconUrl': setButtonIconUrl(value); break;
            case 'categoryImageUrl': setCategoryImageUrl(value); break;
        }

        const wrapperId = categoryDef?.dataConfiguration?.id;
        const actualCategoryId = categoryDef?.dataConfiguration?.categoryId;
        const isEmpty = categoryDef?.dataConfiguration?.isEmpty || false;
        
        if (editorState.activePageId) {
            setContainers(prev => {
                const newContainers = { ...prev };
                const pageContainers = [...(newContainers[editorState.activePageId ?? ''] || [])];
                
                const updateCategoryConfig = (containerList: any[]): any[] => {
                    return containerList.map(container => {
                        const updatedItems = container.content.items?.map((item: any) => {
                            if (item.type === 'category' && item.id === categoryId) {
                                const updatedItem = {
                                    ...item,
                                    configuration: {
                                        ...item.configuration,
                                        [key]: value,
                                        id: wrapperId,
                                        categoryId: actualCategoryId,
                                        isEmpty: isEmpty
                                    },
                                    dataConfiguration: {
                                        ...item.dataConfiguration,
                                        id: wrapperId,
                                        categoryId: actualCategoryId,
                                        isEmpty: isEmpty
                                    }
                                };
                                
                                return updatedItem;
                            }
                            return item;
                        });
                        
                        if (container.content.nestedContainers?.length > 0) {
                            return {
                                ...container,
                                content: {
                                    ...container.content,
                                    items: updatedItems || [],
                                    nestedContainers: updateCategoryConfig(container.content.nestedContainers)
                                }
                            };
                        }
                        
                        return {
                            ...container,
                            content: {
                                ...container.content,
                                items: updatedItems || []
                            }
                        };
                    });
                };
                
                const updatedPageContainers = updateCategoryConfig(pageContainers);
                newContainers[editorState.activePageId ?? ''] = updatedPageContainers;
                
                return newContainers;
            });
        }
        
        if (onConfigChange) {
            // Build complete configuration object with all current values
            const fullConfig = {
                label,
                showTitle,
                titleText,
                showCategoryDescription,
                descriptionText,
                showThumbnail,
                imageDisplay,
                showSortByAttributes,
                sortConfig,
                showInStock,
                inStockText,
                selectByDefault,
                hideIfNoProducts,
                // Product Listing
                style,
                productsPerRow,
                productsLoading,
                scrollBarAfterRows,
                showButtonAfterProducts,
                scrollBarAfterProducts,
                // Product Details
                showModelNumber,
                showProductDescription,
                descriptionStyle,
                showPrice,
                showAvailabilityStatus,
                inStockLabel,
                backOrderLabel,
                showVariants,
                showNumberOfVariants,
                showCategory,
                showManufacturer,
                productDetailsTextAlignment,
                showButtonText,
                buttonLabel,
                showButtonIcon,
                buttonPlacement,
                buttonIconUrl,
                categoryImageUrl,
                id: wrapperId,
                categoryId: actualCategoryId,
                isEmpty: isEmpty,
                [key]: value
            };
            onConfigChange(fullConfig);
        }
    };
    
    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            {/* Header */}
            <div id={`${baseId}-header`} className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-[#D1D1D6] tw-mb-4">
                <div id={`${baseId}-title-wrapper`} className="tw-flex tw-items-center tw-gap-2">
                    <CategoryIcon />
                    <h2 id={`${baseId}-title`} className="tw-text-[#6D788C] tw-text-sm tw-font-semibold">
                        Category
                    </h2>
                </div>
                <button id={`${baseId}-menu-btn`} data-testid={`${baseId}-menu-btn`} className="tw-rounded tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center">
                    <OverFlowIcon />
                </button>
            </div>

            {/* Category Details Section */}
            <CategoryDetails
                id={`${baseId}-details`}
                projectId={projectId}
                modelId={uiModel?.id || ''}
                isExpanded={categoryDetailsExpanded}
                onToggleExpanded={() => setCategoryDetailsExpanded(!categoryDetailsExpanded)}
                label={label}
                showTitle={showTitle}
                titleText={titleText}
                showCategoryDescription={showCategoryDescription}
                descriptionText={descriptionText}
                showThumbnail={showThumbnail}
                imageDisplay={imageDisplay}
                categoryImageUrl={categoryImageUrl}
                showSortByAttributes={showSortByAttributes}
                sortConfig={sortConfig}
                showInStock={showInStock}
                inStockText={inStockText}
                selectByDefault={selectByDefault}
                hideIfNoProducts={hideIfNoProducts}
                attributes={attributes}
                onConfigChange={handleConfigChange}
            />

            {/* Product Listing Section */}
            <ProductListing
                id={`${baseId}-listing`}
                isExpanded={productListingExpanded}
                onToggleExpanded={() => setProductListingExpanded(!productListingExpanded)}
                style={style}
                productsPerRow={productsPerRow}
                productsLoading={productsLoading}
                scrollBarAfterRows={scrollBarAfterRows}
                showButtonAfterProducts={showButtonAfterProducts}
                scrollBarAfterProducts={scrollBarAfterProducts}
                onConfigChange={handleConfigChange}
            />

            {/* Product Details Section */}
            <ProductDetails
                id={`${baseId}-product-details`}
                isExpanded={productDetailsExpanded}
                onToggleExpanded={() => setProductDetailsExpanded(!productDetailsExpanded)}
                showModelNumber={showModelNumber}
                showProductDescription={showProductDescription}
                descriptionStyle={descriptionStyle}
                showPrice={showPrice}
                showAvailabilityStatus={showAvailabilityStatus}
                inStockLabel={inStockLabel}
                backOrderLabel={backOrderLabel}
                showVariants={showVariants}
                showNumberOfVariants={showNumberOfVariants}
                showCategory={showCategory}
                showManufacturer={showManufacturer}
                productDetailsTextAlignment={productDetailsTextAlignment}
                showButtonText={showButtonText}
                buttonLabel={buttonLabel}
                showButtonIcon={showButtonIcon}
                buttonPlacement={buttonPlacement}
                buttonIconUrl={buttonIconUrl}
                onConfigChange={handleConfigChange}
            />
        </div>
    );
};